import {
  ConfirmationDialog,
  date,
  DetailColumns,
  DetailPane,
  FormDialog,
  useDialogs,
  useErrors
} from '@management-ui/core';
import {CopyAll, Delete, Edit, PointOfSale, RestoreFromTrash} from '@mui/icons-material';
import {Link} from '@mui/material';
import {makeStyles} from '@mui/styles';
import {reverse} from 'named-urls';
import * as React from 'react';
import {useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import {useRouteMatch} from 'react-router';
import {Link as RouterLink, useHistory} from 'react-router-dom';
import ConfirmDate from '../../../../components/ConfirmDate';
import OrderAdmin from '../../../../components/OrderAdmin';
import {ServiceContext} from '../../../../components/Services';
import routes from '../../../../routes';
import PurchaseOrderForm from '../../forms/PurchaseOrderForm';
import SalesOrderForm from '../../forms/SalesOrderForm';
import Tabs from './Tabs';
import Wrapper from './Wrapper';

const useStyles = makeStyles(theme => ({
  link: {
    color: theme.palette.primary.main,

    '&:hover': {
      color: theme.palette.secondary.main,
      textDecoration: 'none',
    },
  },
}));

const PurchaseOrder = ({archive = false}) => {
  const classes = useStyles();
  const services = useContext(ServiceContext);
  const history = useHistory();
  const match = useRouteMatch();

  const {errors, toggleError} = useErrors(
    useMemo(() => ({purchaseOrder: 'Sorry the order could not be accessed'}), [])
  );

  const {
    openDialogs,
    toggleDialog
  } = useDialogs(['core', 'lock', 'unlock', 'delete', 'restore', 'duplicate', 'salesOrder']);

  const idRef = useRef('');
  const [loading, setLoading] = useState(false);
  const [order, setOrder] = useState(null);

  const loadOrder = useCallback((id) => {
    if (idRef.current !== `${id}`) {
      idRef.current = `${id}`;
      setLoading(true);
      services.purchaseOrder.getOrder(id, archive).then(retrieved => {
        setOrder(retrieved);
        setLoading(false);
      }).catch(() => {
        toggleError('purchaseOrder', true);
        setLoading(false);
        setTimeout(() => {
          history.push(archive ? reverse(routes.archive.index, {tab: 'purchase-orders'}) : routes.purchaseOrders.index);
        }, 3000);
      });
    }
  }, [archive, services, history, toggleError]);

  useEffect(() => {
    if (match.params?.id) {
      loadOrder(match.params?.id);
    }
  }, [history, match, order, loadOrder]);

  const [title, setTitle] = useState('Loading');
  const [crumbs, setCrumbs] = useState([...(archive ? Wrapper.archiveCrumbs : Wrapper.standardCrumbs)]);

  useEffect(() => {
    let header = 'Loading';
    let trail = [];
    if (order) {
      header = order.reference;
      trail = [
        ...(order.archived ? Wrapper.archiveCrumbs : Wrapper.standardCrumbs),
        {title: header, link: ''}
      ];
    }
    setTitle(header);
    setCrumbs(trail);
  }, [order]);

  const handleSaved = useCallback((updated) => {
    toggleDialog('core', false);
    if (updated) {
      setOrder(updated);
    } else if (order?.id) {
      loadOrder(order.id);
    }
  }, [toggleDialog, setOrder, loadOrder, order]);

  const handleDelete = useCallback((confirmed) => {
    toggleDialog('delete', false);
    if (confirmed) {
      setLoading(true);
      services.purchaseOrder.deleteOrder(order).then(() => {
        setLoading(false);
        history.push(routes.purchaseOrders.index);
      }).catch(() => setLoading(false));
    }
  }, [services, history, toggleDialog, order]);

  const handleRestore = useCallback((confirmed) => {
    toggleDialog('restore', false);
    if (confirmed) {
      setLoading(true);
      services.purchaseOrder.restoreOrder(order).then((restored) => {
        setLoading(false);
        history.push(reverse(routes.purchaseOrders.detail, {id: order.id}));
        setOrder(restored);
      }).catch(() => setLoading(false));
    }
  }, [services, history, toggleDialog, order]);

  const handleDuplicate = useCallback((confirmed) => {
    toggleDialog('duplicate', false);
    if (confirmed) {
      setLoading(true);
      services.purchaseOrder.duplicateOrder(order).then((duplicate) => {
        setLoading(false);
        history.push(reverse(routes.purchaseOrders.detail, {id: duplicate.id}));
        setOrder(duplicate);
      }).catch(() => setLoading(false));
    }
  }, [services, history, toggleDialog, order]);

  const handleSalesOrder = useCallback((salesOrder) => {
    toggleDialog('salesOrder', false);
    history.push(reverse(routes.salesOrders.detail, {id: salesOrder.id}));
  }, [toggleDialog, history]);

  return (
    <Wrapper title={title} order={order} loading={loading} crumbs={crumbs} errors={errors}>
      {order?.id ? (
        <>
          <DetailColumns columns={[

            <DetailPane
              title="Core Details"
              actions={order.archived ? [
                {title: 'Restore', icon: <RestoreFromTrash/>, onClick: () => toggleDialog('restore', true)},
              ] : [
                ...(order.locked ? [] : [
                  {title: 'Edit', icon: <Edit/>, onClick: () => toggleDialog('core', true)},
                  {title: 'Delete', icon: <Delete/>, onClick: () => toggleDialog('delete', true)},
                ]),
                {title: 'Duplicate', icon: <CopyAll/>, onClick: () => toggleDialog('duplicate', true)},
                {title: 'Create Sales order', icon: <PointOfSale/>, onClick: () => toggleDialog('salesOrder', true)}
              ]}
              details={[
                {title: 'Date', value: order.date ? date(order.date) : '-'},
                {
                  title: 'Supplier',
                  value: order.supplier?.name ?? '-',
                  route: order.supplier ? reverse(routes.contacts.detail, {id: order.supplier.id}) : null
                },
                {
                  title: 'Loading Date',
                  value: (
                    <ConfirmDate
                      order={order}
                      field="collection_date"
                      service={services.purchaseOrder.saveOrder}
                      onUpdated={setOrder}
                    />
                  )
                },
                {title: 'PO Number', value: order.reference ?? '-'},
                {title: 'Load Number', value: order.load_number ?? '-'},
                {title: 'Currency', value: order.currency},
                ...(order.sales_orders.length > 0 ? [{
                  title: 'Sales Order(s)',
                  value: (
                    <span>
                      {order.sales_orders.map((salesOrder, index) => (
                        <span key={index}>
                          <Link
                            component={RouterLink}
                            className={classes.link}
                            to={reverse(routes.salesOrders.detail, {id: salesOrder.id})}
                          >{salesOrder.reference}</Link>&nbsp;
                        </span>
                      ))}
                    </span>
                  ),
                }] : []),
              ]}
              dialogs={[
                (props) => (
                  <FormDialog
                    {...props}
                    title="Edit Purchase Order"
                    open={openDialogs['core'] ?? false}
                    onClose={() => toggleDialog('core', false)}
                    render={(props) => (
                      <PurchaseOrderForm
                        {...props}
                        order={order}
                        fields="core"
                        onSaved={handleSaved}
                      />
                    )}
                  />
                ),
                (props) => (
                  <ConfirmationDialog
                    {...props}
                    open={openDialogs['delete'] ?? false}
                    title="Delete Purchase Order"
                    message="Are you sure you want to delete this order?"
                    onClose={handleDelete}
                  />
                ),
                (props) => (
                  <ConfirmationDialog
                    {...props}
                    open={openDialogs['restore'] ?? false}
                    title="Restore Purchase Order"
                    message="Are you sure you want to restore this order?"
                    onClose={handleRestore}
                  />
                ),
                (props) => (
                  <ConfirmationDialog
                    {...props}
                    open={openDialogs['duplicate'] ?? false}
                    title="Duplicate Purchase Order"
                    message="Are you sure you want to duplicate this order?"
                    onClose={handleDuplicate}
                  />
                ),
                (props) => (
                  <FormDialog
                    {...props}
                    title="Create New Sales Order from Purchase Order"
                    open={openDialogs['salesOrder'] ?? false}
                    onClose={() => toggleDialog('salesOrder', false)}
                    render={(props) => (
                      <SalesOrderForm
                        {...props}
                        order={{currency: order.currency}}
                        fields="core"
                        onSaved={handleSalesOrder}
                        purchaseOrder={order}
                      />
                    )}
                  />
                )
              ]}
            />,

            <OrderAdmin
              type="Purchase"
              order={order}
              onOrderUpdated={setOrder}
              service={services.purchaseOrder}
              onLoading={setLoading}
            />,
          ]}/>

          <Tabs order={order} onOrderUpdated={setOrder}/>
        </>
      ) : null}
    </Wrapper>
  );
};

export default PurchaseOrder;
